import { useEffect, useState } from "react";
import {
  clientApiService,
  DocumentDetail,
} from "../../services/client-api.service";
import { SignaturePad } from "./SignaturePad";

interface DocumentViewerModalProps {
  documentId: string;
  onClose: () => void;
  onSigned?: () => void;
}

const ETL_URL =
  import.meta.env.VITE_API_URL_ETL || "https://worker-etl.betalog-ventas.workers.dev";

function getSignatureUrl(documentId: string) {
  return `${ETL_URL}/api/public/photos/signatures/${documentId}`;
}

export function DocumentViewerModal({
  documentId,
  onClose,
  onSigned,
}: DocumentViewerModalProps) {
  const [doc, setDoc] = useState<DocumentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signatureSvg, setSignatureSvg] = useState<string | null>(null);
  const [signedName, setSignedName] = useState("");
  const [isSigning, setIsSigning] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await clientApiService.getDocumentDetail(documentId);
      setDoc(data);
    } catch (err: any) {
      setError(err?.message || "Error al cargar documento");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSigning) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, isSigning]);

  const handleSign = async () => {
    if (!doc || !signatureSvg || !signedName.trim()) {
      setSignError("Firma y nombre completo son requeridos");
      return;
    }
    if (signedName.trim().length < 3) {
      setSignError("El nombre debe tener al menos 3 caracteres");
      return;
    }

    setIsSigning(true);
    setSignError(null);
    try {
      await clientApiService.signDocument(doc.id, {
        signatureSvg,
        signedName: signedName.trim(),
      });
      await load();
      setShowSignaturePad(false);
      setSignatureSvg(null);
      setSignedName("");
      if (onSigned) onSigned();
    } catch (err: any) {
      setSignError(err?.message || "Error al firmar");
    } finally {
      setIsSigning(false);
    }
  };

  const isImage = (url: string) => /\.(png|jpg|jpeg|webp|gif|svg)(\?|$)/i.test(url);
  const isPdf = (url: string) => /\.pdf(\?|$)/i.test(url);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={!isSigning ? onClose : undefined}
    >
      <div
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-slate-900 truncate">
              {doc?.name ?? "Cargando..."}
            </h3>
            {doc && (
              <p className="text-xs text-slate-500 mt-0.5">
                {doc.projectName} • {doc.category} •{" "}
                {new Date(doc.createdAt).toLocaleDateString("es-MX")}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isSigning}
            className="text-slate-400 hover:text-slate-700 text-2xl leading-none disabled:opacity-50"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error || !doc ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
              {error || "Documento no encontrado"}
            </div>
          ) : (
            <div className="space-y-5">
              {/* Estado */}
              <div className="flex items-center gap-2 flex-wrap">
                {doc.requiresSignature && !doc.signed && (
                  <span className="text-xs font-semibold bg-yellow-100 text-yellow-700 px-2.5 py-1 rounded-full">
                    ⏳ Requiere tu firma
                  </span>
                )}
                {doc.signed && (
                  <span className="text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                    ✅ Firmado el{" "}
                    {new Date(doc.signedAt!).toLocaleDateString("es-MX")}
                  </span>
                )}
                <span className="text-xs text-slate-500">{doc.category}</span>
              </div>

              {/* Vista previa */}
              <div className="bg-slate-100 rounded-xl overflow-hidden min-h-[300px] flex items-center justify-center">
                {isImage(doc.url) ? (
                  <img
                    src={doc.url}
                    alt={doc.name}
                    className="max-w-full max-h-[500px] object-contain"
                  />
                ) : isPdf(doc.url) ? (
                  <iframe
                    src={doc.url}
                    title={doc.name}
                    className="w-full h-[500px]"
                  />
                ) : (
                  <div className="text-center p-12">
                    <div className="text-5xl mb-3">📄</div>
                    <p className="text-slate-600 font-medium mb-4">
                      Este tipo de archivo no se puede previsualizar
                    </p>
                  </div>
                )}
              </div>

              {/* Acciones */}
              <div className="flex flex-wrap gap-2">
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-medium transition"
                >
                  👁️ Abrir en nueva pestaña
                </a>
                <a
                  href={doc.url}
                  download={doc.name}
                  className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-medium transition"
                >
                  ⬇️ Descargar
                </a>
                {doc.requiresSignature && !doc.signed && !showSignaturePad && (
                  <button
                    onClick={() => setShowSignaturePad(true)}
                    className="text-sm bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg font-semibold transition"
                  >
                    ✍️ Firmar documento
                  </button>
                )}
              </div>

              {/* Info de firma */}
              {doc.signed && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <h4 className="font-semibold text-green-800 mb-2 text-sm">
                    ✅ Documento firmado
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-green-700/70">Firmado por:</span>
                      <span className="ml-2 font-medium text-green-900">
                        {doc.signedByName ?? "—"}
                      </span>
                    </div>
                    <div>
                      <span className="text-green-700/70">Fecha:</span>
                      <span className="ml-2 font-medium text-green-900">
                        {new Date(doc.signedAt!).toLocaleString("es-MX")}
                      </span>
                    </div>
                  </div>
                  {doc.metadata?.signedName && (
                    <div className="mt-2">
                      <span className="text-green-700/70 text-sm">Nombre registrado:</span>
                      <span className="ml-2 font-medium text-green-900 text-sm">
                        {doc.metadata.signedName}
                      </span>
                    </div>
                  )}
                  {doc.signed && (
                    <div className="mt-3">
                      <p className="text-xs text-green-700/70 mb-1">Firma registrada:</p>
                      <img
                        src={getSignatureUrl(doc.id)}
                        alt="Firma"
                        className="bg-white rounded border border-green-200 max-h-[100px]"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Canvas de firma */}
              {showSignaturePad && !doc.signed && (
                <div className="border-t border-slate-200 pt-5 space-y-4">
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">
                      ✍️ Firma electrónica simple
                    </h4>
                    <p className="text-xs text-slate-500 mb-4">
                      Al firmar, aceptas el contenido de este documento. Tu firma,
                      IP y navegador quedarán registrados como evidencia.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Nombre completo (como aparecerá en la firma){" "}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      value={signedName}
                      onChange={(e) => setSignedName(e.target.value)}
                      placeholder="Tu nombre completo"
                      maxLength={120}
                      disabled={isSigning}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Firma <span className="text-primary">*</span>
                    </label>
                    <SignaturePad
                      onChange={(svg) => setSignatureSvg(svg)}
                      height={200}
                    />
                  </div>

                  {signError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                      {signError}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowSignaturePad(false);
                        setSignatureSvg(null);
                        setSignedName("");
                        setSignError(null);
                      }}
                      disabled={isSigning}
                      className="px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSign}
                      disabled={isSigning || !signatureSvg || !signedName.trim()}
                      className="px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50"
                    >
                      {isSigning ? "Firmando..." : "✅ Confirmar firma"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}